export type SalesInsightsDeltaDirection = 'up' | 'down' | 'flat';

export interface SalesInsightsDelta {
  percent: number;
  text: string;
  direction: SalesInsightsDeltaDirection;
}

const NO_BREAK_SPACE = ' ';
const MINUS_SIGN = '−';

const amountFormat = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
const compactFormat = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 1 });
const percentFormat = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function formatSalesInsightsAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return formatWithTypography(amountFormat, value);
}

export function formatSalesInsightsCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  const size = Math.abs(value);
  if (size >= 1_000_000) {
    return `${formatWithTypography(compactFormat, value / 1_000_000)}${NO_BREAK_SPACE}M`;
  }
  if (size >= 1_000) {
    return `${formatWithTypography(compactFormat, value / 1_000)}${NO_BREAK_SPACE}k`;
  }
  return formatWithTypography(amountFormat, value);
}

export function formatSalesInsightsPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return `${formatWithTypography(amountFormat, Math.round(value))}${NO_BREAK_SPACE}%`;
}

function formatWithTypography(format: Intl.NumberFormat, value: number): string {
  return format
    .formatToParts(value)
    .map(part => (part.type === 'group' ? NO_BREAK_SPACE : part.type === 'minusSign' ? MINUS_SIGN : part.value))
    .join('');
}

export function salesInsightsDelta(
  current: number | null | undefined,
  previous: number | null | undefined
): SalesInsightsDelta | null {
  if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) {
    return null;
  }
  const percent = ((current - previous) / Math.abs(previous)) * 100;
  const rounded = Math.round(percent * 10) / 10;
  const direction: SalesInsightsDeltaDirection = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
  const sign = direction === 'up' ? '+' : direction === 'down' ? MINUS_SIGN : '';
  return {
    percent: rounded,
    text: `${sign}${percentFormat.format(Math.abs(rounded))}${NO_BREAK_SPACE}%`,
    direction
  };
}
